<template>
  <div class="sw-modal">
    <div v-click-outside="cancel" class="sw-modal-content edit-template">
      <div class="row sw-modal-heading">
        <div class="col">
          <h4>
            <i class="fas fa-file-alt"></i>
            <span v-if="viewingTemplate">View Template</span>
            <span v-else>Add a Template</span>
          </h4>
        </div>
      </div>
      <div class="sw-modal-body template" v-if="template">
        <ValidationObserver ref="whatsAppTemplateForm" tag="form">
          <ul>
            <li class="row">
              <label for="name" class="col-12">Name</label>
              <div id="name" class="col-12">
                <span v-if="viewingTemplate">{{template.name}}</span>
                <ValidationProvider v-else
                                    mode="eager"
                                    v-bind:rules="{ required: true, min: 3, regex: /^[a-zA-Z0-9_-]{2,}$/ }"
                                    v-slot="{ errors }">
                  <input type="text" v-model="template.name" v-on:keydown="checkCharacter" v-on:keyup="replaceSpacesInNameAndCovertToLowerCase" ref="nameInput" class="short-question">
                  <span class="form__error" v-if="errors">{{ errors[0] }}</span>
                </ValidationProvider>

              </div>
            </li>

            <template v-if="viewingTemplate">
              <li class="row">
                <div class="col-6">
                  <label for="language-read-only">Language</label>
                  <div id="language-read-only">{{getLanguageNameFromCode(template.language)}}</div>
                </div>
                <div class="col-6">
                  <label for="category-read-only">Category</label>
                  <div id="category-read-only">{{template.category}}</div>
                </div>
              </li>
              <li class="row" v-if="viewingTemplate">
                <div class="col-6">
                  <label for="status" class="">Status</label>
                  <div id="status" v-bind:class="{'template__status--approved' : isApproved, 'template__status--under-review' : isUnsubmitted || isReceived || isPending, 'template__status--rejected' : isRejected || isPaused || isDisabled}">
                    <span v-html="statusForDisplay"></span>
                  </div>
                </div>
                <div class="col-6">
                  <label for="lastUpdated" class="">Last Updated</label>
                  <div id="lastUpdated" class="">
                    <span v-if="template.lastUpdated">{{friendlyDate(template.lastUpdated)}}</span>
                    <span v-else>--</span>
                  </div>
                </div>
              </li>
            </template>
            <template v-else>
              <li class="row">
                <div class="col-12">
                  <label for="language">Language</label>
                  <div id="language">
                    <select v-model="template.language">
                      <option v-for="(language, index) of getLanguages()" v-bind:key="index"
                              v-bind:value="language.code">{{language.name}}
                      </option>
                    </select>
                  </div>
                </div>
              </li>
            </template>

            <li class="row">
              <label for="content" class="col-12">Content
                <variables v-if="!viewingTemplate" v-bind:variables="variablesCopy" v-bind:obj="template"
                           v-bind:propertyName="'content'" v-bind:inputTextArea="contentInput"
                           v-bind:popupPosition="'bottom-right'"></variables>
              </label>
              <div id="content" class="col-12 template__content" v-bind:class="{'template__content--read-only' : viewingTemplate}">
                <span v-if="viewingTemplate" v-html="contentHTML"></span>
                <ValidationProvider v-else rules="required|min:10|max:1024" v-slot="{ errors }" tag="div">
                  <textarea ref="content" v-model="template.content" class="long-question"></textarea>
                  <div class="template__content__chars-remaining">
                    <span class="" v-bind:class="{'sms-template__chars-remaining-count--negative' : remainingCharacters < 0}">{{remainingCharacters}}</span> characters left
                  </div>
                  <span class="form__error" v-show="errors">{{ errors[0] }}</span>
                  <span class="text-note"><b>Note:</b> Please adhere to the <a href="#" @click.stop="showTemplateGuidelines = true">WhatsApp Template Guidelines</a>.</span>
                  <TemplateGuidelines v-if="showTemplateGuidelines" @hide-template-guidelines="showTemplateGuidelines = false"/>
                </ValidationProvider>
              </div>
            </li>

            <li class="row">
              <div class="template__preview">
                <label for="preview" class="col-12">Preview</label>
                <div class="col-12 template__preview-note">
                  <span class="text-note">Variables in your message are replaced with sample data and highlighted in </span>
                  <span class='template__preview-variable'>blue</span>.
                </div>
                <div id="preview" class="col-12 template__preview-phone">
                  <div class="template__preview-bubble" v-html="previewContentHTML"></div>
                </div>
              </div>
            </li>

<!--            <li v-if="!viewingTemplate" class="row">-->
<!--              <div class="">-->
<!--                <label for="submitForApproval" class="col-12">WhatsApp Approval</label>-->
<!--                <div class="col-12">-->
<!--                  To use this Template in an Automation, it needs to be approved by WhatsApp. WhatsApp usually-->
<!--                  approves or rejects template submissions in 24 hours or less.-->
<!--                </div>-->
<!--                <div class="col-12">-->
<!--                  <input id="submitForApproval" type="checkbox" v-model="submitForApproval"/>-->
<!--                </div>-->
<!--              </div>-->
<!--            </li>-->

          </ul>

          <div v-show="waiting">
            <Spinner/>
          </div>

          <div v-show="!waiting" class="col-12">
            <div v-if="error">
              <div class="col-12 alert alert-danger" role="alert">
                {{error}}
              </div>
              <button v-on:click.prevent="cancel" class="btn btn-secondary">Close</button>
            </div>
            <div v-else-if="success">
              <div class="col-12 alert alert-success" role="alert">
                {{success}}
              </div>
              <button v-on:click.prevent="$root.$emit('finished-editing-template')" class="btn btn-secondary">Done</button>
            </div>
            <div v-else class="row justify-content-center">
              <template v-if="viewingTemplate">
                <div class="col-6">
                  <button v-on:click.prevent="cancel" class="btn btn-secondary">Close</button>
                </div>
                <div class="col-6" v-if="isUnsubmitted && isWhatsAppActive">
                  <button v-on:click.prevent.stop="submitTemplate()" class="btn btn-primary">Submit <span class="d-none d-md-inline">to WhatsApp</span></button>
                </div>
              </template>
              <template v-else>
                <div class="col-6">
                  <button v-on:click.prevent="cancel" class="btn btn-secondary">Cancel</button>
                </div>
                <div class="col-6">
                  <button v-on:click.prevent="saveTemplate()" class="btn btn-primary">
                    <template v-if="template.reference">Save</template>
                    <template v-else>Create</template>
                  </button>
                </div>
                <div>
                  <span class="text-note">You can submit a template for approval after creating it</span>
                </div> 
              </template>
            </div>
          </div>
        </ValidationObserver>
      </div>
    </div>
  </div>
</template>

<script>
    import { store } from '../../store.js'
    import { ValidationObserver, ValidationProvider, /*extend*/ } from 'vee-validate'

    import Spinner from '../../components/Spinner.vue'
    import Variables from "../../components/establishments/Variables";
    import TemplateGuidelines from "../../components/whatsapp/TemplateGuidelines.vue";

    import {datesMixin} from "../../mixins/DatesMixin"
    import {variablesMixin} from '../../mixins/VariablesMixin'
    import {languagesMixin} from '../../mixins/LanguagesMixin'
    import {whatsAppMixin} from '../../mixins/WhatsAppMixin'
    import {whatsAppTemplateMixin} from '../../mixins/WhatsAppTemplateMixin'

    export default {
        name: 'EditTemplate',
        components: {
            Spinner, Variables, ValidationObserver, ValidationProvider, TemplateGuidelines
            // Draggable, Spinner, QuestionPreview, Markdown
        },
        mixins: [datesMixin, variablesMixin, languagesMixin, whatsAppTemplateMixin, whatsAppMixin],
        props: {
            template: Object
        },
        data: function () {
            return {
                contentInput: null,
                showTemplateGuidelines: false,
                waiting: false,
                error: null,
                success: null
            }
        },
        computed: {
            viewingTemplate: function() {
                if (this.template && this.template.reference && this.template.contentSid) {
                    return true;
                }
                return false;
            },
            remainingCharacters: function() {
                return 1024 - this.template.content.length;
            },
            contentHTML: function() {
                let previewText = this.template.content;
                previewText = previewText.replace(/\n/g, '<br/>');
                return previewText;
            },
            previewContentHTML: function() {
                let previewText = this.template.content;
                let variables = previewText.matchAll(/\${(.+?)}/g);
                if (variables) {
                    for (let variable of variables) {
                        let value = this.getExampleForVariable(variable[1]);
                        previewText = previewText.replaceAll(variable[0], "<span class='template__preview-variable'>" + value + "</span>");
                    }
                }
                previewText = previewText.replace(/\n/g, '<br/>');
                return previewText;
            }
        },
        methods: {
            checkCharacter: function(event) {
                const regex = /^[a-zA-Z0-9_ ]+$/;
                if (event.key.length === 1 && !regex.test(event.key)) {
                    event.preventDefault();
                }
            },
            replaceSpacesInNameAndCovertToLowerCase: function() {
                this.template.name = this.template.name.replace(/ /g,"_");
                this.template.name = this.template.name.toLowerCase();
            },
            saveTemplate: async function() {
                const success = await this.$refs.whatsAppTemplateForm.validate();
                if (!success) {
                    return
                }

                console.log("Saving template");
                this.waiting = true;
                this.error = null;

                let body = {
                    name: this.template.name,
                    category: this.template.category,
                    language: this.template.language,
                    contentType: 'text',
                    content: this.template.content
                };
                if (this.template.reference) {
                    body.reference = this.template.reference;
                } 

                const method = this.template.reference ? 'put' : 'post';
                this.$http[method]('/guest-management/v1/establishments/' + store.currentEstablishmentReference + '/templates', body
                ).then(response => {
                    console.log("Template created / updated. Response: " + response);
                    this.waiting = false;
                    this.success = "Template added";
                    setTimeout(() => {
                        this.$root.$emit('finished-editing-template', response.data);
                    }, 2000)
                }).catch(error => {
                    this.waiting = false
                    if (error.response) {
                        this.error = "Error: " + error.response.data.message
                    } else {
                        this.error = "Sorry, we can't seem to reach our servers! Please try again"
                    }
                })

            },
            submitTemplate: function() {
                console.log("Start Submitting Template");
                this.$root.$emit('start-submitting-template', this.template);
            },
            cancel: function() {
                console.log("Cancelling");
                this.$root.$emit('finished-editing-template');
            }
        },
        mounted: async function() {
            await this.fetchVariables();
            await this.fetchWhatsApp(store.currentEstablishmentReference);
            if (!this.viewingTemplate) {
              this.contentInput = this.$refs.content;
              this.$refs.nameInput.focus();
            }
        }
    }

</script>

<style lang="scss" scoped>
  @import "../../styles/components/modal";
  @import "../../styles/components/question_answer";
  @import "../../styles/components/edit_template";


  // Todo: ::v-deep Deprecated in Vue 3
  .template__preview ::v-deep .template__preview-variable {
    display: inline-block;
    border: 1px dotted $blue-dark;
    background-color: $blue-light;
    padding: 0 3px;
    color: $black;
  }
</style>